import React, { useState, useEffect, useRef, useCallback } from 'react';
import { activityLogService } from '../../services/api';
import { ActivityLog as ActivityLogType, ActivityActionType } from '../../types';
import DatePicker from 'react-datepicker';
import '../../styles/ActivityLog.css';

const ActivityLog: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLogType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Last 7 days
    endDate: new Date(),
    userId: '',
    action: '',
    entity: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [liveUpdates, setLiveUpdates] = useState<boolean>(false);
  const wsRef = useRef<WebSocket | null>(null);

  // Define fetchLogs with useCallback to avoid dependency issues
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : undefined,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        entity: filters.entity || undefined,
        search: filters.search || undefined
      };
      
      const response = await activityLogService.getLogs(params);
      
      // Map the logs with proper field mapping from the database to our frontend model
      const processedLogs = response.data.logs.map((log: Record<string, any>) => {
        // Create a processed log that matches our frontend interface
        const processedLog: ActivityLogType = {
          id: log.id || 0,
          user_id: log.user_id || null,
          action: log.action || '',
          resource_type: log.table_name || 'unknown',
          resource_id: log.record_id || null,
          description: generateDescription(log),
          ip_address: log.ip_address || null,
          user_agent: log.user_agent || null,
          timestamp: log.timestamp || new Date().toISOString(),
          // Store old_data and new_data in additional_data for detailed view
          additional_data: {
            old_data: log.old_data,
            new_data: log.new_data
          },
          User: log.User
        };
        
        return processedLog;
      });
      
      setLogs(processedLogs);
      setPagination(prev => ({
        ...prev,
        totalPages: Math.ceil(response.data.pagination.total / response.data.pagination.limit),
        totalItems: response.data.pagination.total
      }));
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  // Fetch logs initially and when filters change
  useEffect(() => {
    fetchLogs();
    
    // Clean up WebSocket connection if active
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchLogs]);

  // Setup WebSocket for live updates when enabled
  useEffect(() => {
    if (liveUpdates) {
      // Initialize WebSocket - replace with actual WebSocket endpoint
      const ws = new WebSocket('ws://localhost:5000/api/activity-logs/live');
      
      ws.onopen = () => {
        // WebSocket connection established
      };
      
      ws.onmessage = (event) => {
        try {
          const newLog = JSON.parse(event.data) as ActivityLogType;
          setLogs(prevLogs => [newLog, ...prevLogs.slice(0, 49)]); // Keep max 50 items in state
        } catch (error) {
          // Silently handle parsing errors
        }
      };
      
      ws.onerror = () => {
        // Handle WebSocket error silently
        setLiveUpdates(false);
      };
      
      ws.onclose = () => {
        // WebSocket connection closed
      };
      
      wsRef.current = ws;
      
      return () => {
        ws.close();
      };
    } else if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, [liveUpdates]);

  // fetchLogs moved above using useCallback
  
  // Generate a meaningful description based on action and data
  const generateDescription = (log: Record<string, any>): string => {
    const { action, table_name, record_id, old_data, new_data } = log as {
      action: string;
      table_name?: string;
      record_id?: string | number;
      old_data?: Record<string, any>;
      new_data?: Record<string, any>;
    };
    
    // Return early if we don't have enough data
    if (!table_name) return 'No description';
    
    // Format table name for better readability
    const entity = table_name ? table_name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'Unknown';
    
    switch(action) {
      case 'insert':
        if (new_data) {
          const descriptionField = new_data.description || new_data.name || '';
          return `Created new ${entity}: ${descriptionField}`;
        }
        return `Created new ${entity} #${record_id}`;
        
      case 'update':
        if (old_data && new_data) {
          // Find the changed fields
          const changes = [];
          for (const key in new_data) {
            if (JSON.stringify(old_data[key]) !== JSON.stringify(new_data[key])) {
              changes.push(key);
            }
          }
          return `Updated ${entity} #${record_id}: Changed ${changes.join(', ')}`;
        }
        return `Updated ${entity} #${record_id}`;
        
      case 'delete':
        return `Deleted ${entity} #${record_id}`;
        
      case 'restore':
        return `Restored ${entity} #${record_id}`;
        
      case 'login':
        return 'User logged in';
        
      case 'logout':
        return 'User logged out';
        
      case 'view':
        return `Viewed ${entity}${record_id ? ` #${record_id}` : 's'}`;
        
      case 'export':
        return `Exported ${entity}${record_id ? ` #${record_id}` : ''}`;
        
      default:
        return `${action.charAt(0).toUpperCase() + action.slice(1)} ${entity}${record_id ? ` #${record_id}` : ''}`;
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };

  const handleDateChange = (name: string, date: Date | null) => {
    setFilters(prev => ({ ...prev, [name]: date }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleLiveUpdates = () => {
    setLiveUpdates(!liveUpdates);
  };

  const changePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const formatDateTime = (dateTime: string) => {
    try {
      const date = new Date(dateTime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateTime || 'Invalid date';
    }
  };

  const getBadgeColor = (action: string) => {
    switch(action.toLowerCase()) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'danger';
      case 'view':
        return 'secondary';
      case 'export':
      case 'import':
        return 'warning';
      case 'login':
        return 'success';
      case 'logout':
        return 'secondary';
      case 'access_denied':
        return 'danger';
      case 'error':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="activity-log-page">
      <div className="activity-log-header">
        <h2>Activity Log</h2>
        <div className="live-update-toggle">
          <label className={`toggle-switch ${liveUpdates ? 'active' : ''}`}>
            <input
              type="checkbox"
              checked={liveUpdates}
              onChange={toggleLiveUpdates}
              aria-label="Toggle live updates"
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="live-text">Live Updates</span>
        </div>
      </div>

      <div className="filter-panel">
        <div className="filter-header">
          <h3>
            <i className="fas fa-filter"></i> Filters
          </h3>
        </div>
        
        <div className="filter-content">
          <div className="filter-row">
            <div className="filter-group">
              <label>Date Range:</label>
              <div className="date-picker-container">
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => handleDateChange('startDate', date)}
                  selectsStart
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Start date"
                  className="date-picker"
                />
                <span className="date-separator">to</span>
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => handleDateChange('endDate', date)}
                  selectsEnd
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  minDate={filters.startDate}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="End date"
                  className="date-picker"
                />
              </div>
            </div>
          </div>
          
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="action">Action:</label>
              <select
                id="action"
                name="action"
                value={filters.action}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Actions</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="entity">Entity:</label>
              <select
                id="entity"
                name="entity"
                value={filters.entity}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Entities</option>
                <option value="record">Records</option>
                <option value="category">Categories</option>
                <option value="exchange_rate">Exchange Rates</option>
                <option value="user">Users</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="userId">User:</label>
              <input
                type="text"
                id="userId"
                name="userId"
                value={filters.userId}
                onChange={handleFilterChange}
                placeholder="User ID"
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search details..."
                className="filter-input"
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading activity logs...</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="table activity-log-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Entity ID</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map(log => (
                    <tr key={log.id}>
                      <td className="timestamp">{formatDateTime(log.timestamp)}</td>
                      <td>
                        {log.User?.name || 'Unknown'}
                        <small className="user-id">{log.user_id || 'N/A'}</small>
                      </td>
                      <td>
                        <span className={`badge ${getBadgeColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>{log.resource_type || 'N/A'}</td>
                      <td>{log.resource_id || 'N/A'}</td>
                      <td className="details-cell">
                        <div className="details-wrapper">
                          {log.description || 'No description'}
                          {log.additional_data && (
                            <details>
                              <summary>Additional data</summary>
                              <pre className="additional-data">
                                {typeof log.additional_data === 'string'
                                  ? log.additional_data
                                  : JSON.stringify(log.additional_data, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="no-logs">
                      No activity logs found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {logs.length > 0 && (
            <div className="pagination">
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => changePage(1)}
                disabled={pagination.page === 1}
                title="Go to first page"
                aria-label="Go to first page"
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => changePage(pagination.page - 1)}
                disabled={pagination.page === 1}
                title="Go to previous page"
                aria-label="Go to previous page"
              >
                <i className="fas fa-angle-left"></i>
              </button>
              
              <span className="page-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.totalItems} items)
              </span>
              
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => changePage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                title="Go to next page"
                aria-label="Go to next page"
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => changePage(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
                title="Go to last page"
                aria-label="Go to last page"
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          )}
        </>
      )}
      

    </div>
  );
};

export default ActivityLog;
